import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import UnifiedEditor from '../components/UnifiedEditor';
import {
  CollaborativeRunnerProvider,
  CollaborativeRunnerInputSection,
  CollaborativeRunnerOutputSection,
  OutputBlockRunInfo,
  useRunnerContext,
  EditorInputFilesBadge,
  RunnerSessionTaskAttachmentsSync,
} from '../components/CollaborativeRunner';
import CollaborativeTaskPanel from '../components/CollaborativeTaskPanel';
import CollaborativeSessionTaskList from '../components/CollaborativeSessionTaskList';
import CollaborativeAllTasksPanel from '../components/CollaborativeAllTasksPanel';
import WorkPageBlocksLayout, {
  LAYOUT_PRESET_A,
  LAYOUT_PRESET_B,
  saveLayoutToStorage,
  loadLayoutFromStorage,
  layoutsEqual,
} from '../components/WorkPageBlocksLayout';
import { useWorkChrome } from '../context/WorkChromeContext';
import Modal from '../components/Modal';
import TaskStatementMeta from '../components/TaskStatementMeta';
import TaskAnswerCheckPanel from '../components/TaskAnswerCheckPanel';
import ChoiceAnswerPanel from '../components/ChoiceAnswerPanel';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { isChoiceAnswerType } from '../utils/workAttemptHelpers';

function normalizeEditorSettings(settings) {
  const source = settings && typeof settings === 'object' ? settings : {};
  return {
    ...source,
    lineWrapping: source.lineWrapping === true,
    taskTextWrapping: source.taskTextWrapping !== false,
  };
}

/** Текст эталона для вставки в редактор: код, иначе разбор (с сохранением отступов в <pre><code>). */
function solutionClipboardText({ solution_code, solution_explanation }) {
  const code = typeof solution_code === 'string' ? solution_code.trim() : '';
  if (code) return code;
  const raw = typeof solution_explanation === 'string' ? solution_explanation : '';
  if (!raw.trim()) return '';

  const blocks = [];
  const preCodeRe = /<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi;
  let m;
  while ((m = preCodeRe.exec(raw)) !== null) {
    blocks.push(m[1]);
  }
  if (!blocks.length) {
    const codeRe = /<code[^>]*>([\s\S]*?)<\/code>/gi;
    while ((m = codeRe.exec(raw)) !== null) {
      blocks.push(m[1]);
    }
  }
  if (blocks.length) {
    const decode = (s) => {
      const el = document.createElement('textarea');
      el.innerHTML = s.replace(/<br\s*\/?>/gi, '\n');
      return el.value.replace(/\u00a0/g, ' ').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    };
    return blocks.map(decode).join('\n\n').trim();
  }

  // Нет code-блоков — обычный текст без схлопывания отступов внутри строк.
  const withBreaks = raw
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n')
    .replace(/<\/\s*div\s*>/gi, '\n')
    .replace(/<\/\s*li\s*>/gi, '\n');
  return withBreaks
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function CollaborativePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentSessionId, setCurrentSessionId] = useState(sessionId || '');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [ydoc, setYdoc] = useState(null);
  const runRequestRef = useRef(null);
  const editorRef = useRef(null);
  const allTasksPanelRef = useRef(null);
  const [collabConnected, setCollabConnected] = useState(false);
  const [collabUserCount, setCollabUserCount] = useState(0);
  const [styleLintEnabled, setStyleLintEnabled] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const layoutStorageKey = user ? `workLayout:${user.username}` : 'workLayout:guest';
  const inputStateKey = user ? `workInputState:${user.username}` : 'workInputState:guest';
  const taskStatementStateKey = user ? `workTaskStatementState:${user.username}` : 'workTaskStatementState:guest';
  const taskListStateKey = user ? `workTaskListState:${user.username}` : 'workTaskListState:guest';
  const allTasksStateKey = user ? `workAllTasksState:${user.username}` : 'workAllTasksState:guest';
  const [layout, setLayout] = useState(LAYOUT_PRESET_A);
  const [inputCollapsed, setInputCollapsed] = useState(false);
  const [inputCollapsedHeight, setInputCollapsedHeight] = useState(6);
  const [taskStatementCollapsed, setTaskStatementCollapsed] = useState(false);
  const [taskStatementCollapsedHeight, setTaskStatementCollapsedHeight] = useState(6);
  const [taskListCollapsed, setTaskListCollapsed] = useState(false);
  const [taskListCollapsedHeight, setTaskListCollapsedHeight] = useState(9);
  const [allTasksCollapsed, setAllTasksCollapsed] = useState(false);
  const [allTasksCollapsedHeight, setAllTasksCollapsedHeight] = useState(9);
  const [taskListRefreshNonce, setTaskListRefreshNonce] = useState(0);
  const [allTasksRefreshNonce, setAllTasksRefreshNonce] = useState(0);
  const [draftPrompt, setDraftPrompt] = useState(null); // { key, value }
  const [activeTaskTitle, setActiveTaskTitle] = useState('Текст задачи');
  const [activeTaskAttachments, setActiveTaskAttachments] = useState([]);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [activeTaskMeta, setActiveTaskMeta] = useState({
    tags: [],
    source_name: '',
    source_number: '',
    source_author: '',
    year: null,
    answer_type: '',
    expected_answer: '',
    answer_spec: {},
    solution_code: '',
    solution_explanation: '',
  });
  const [solutionCopyHint, setSolutionCopyHint] = useState('');
  const solutionCopyHintTimerRef = useRef(null);
  const [taskTextEditMode, setTaskTextEditMode] = useState(false);
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [wrapSaving, setWrapSaving] = useState(false);
  const [editorThemeSaving, setEditorThemeSaving] = useState(false);
  const [wrapSaveError, setWrapSaveError] = useState('');
  const draftSaveTimeoutRef = useRef(null);
  const draftCheckedRef = useRef(false);

  const [editorSettings, setEditorSettings] = useState(() => normalizeEditorSettings(user?.editor_settings));
  useEffect(() => {
    setEditorSettings(normalizeEditorSettings(user?.editor_settings));
  }, [user?.editor_settings]);
  const editorTheme = editorSettings?.themeId ?? 'dark';
  /** Каталог и управление списком сессии — не только «преподаватель», но и staff/admin (как в EditorRoute). */
  const canManageCollabTasks =
    !!user?.is_teacher || !!user?.is_staff || !!user?.is_admin;
  const taskTextWrapping = editorSettings.taskTextWrapping !== false;

  // Подписка на sessionMeta (Yjs): обновляем метаданные активной задачи у ВСЕХ участников,
  // даже если они не запрашивают API /tasks/ в момент переключения преподавателем.
  useEffect(() => {
    if (!ydoc) return;
    const meta = ydoc.getMap('sessionMeta');
    const apply = () => {
      const title = meta.get('activeTaskTitle');
      const tagsJson = meta.get('activeTaskTagsJson');
      const source_name = meta.get('activeSourceName');
      const source_number = meta.get('activeSourceNumber');
      const source_author = meta.get('activeSourceAuthor');
      const yearRaw = meta.get('activeYear');
      const expectedRaw = meta.get('activeExpectedAnswer');
      const answerTypeRaw = meta.get('activeAnswerType');
      const answerSpecRaw = meta.get('activeAnswerSpecJson');
      const solutionCodeRaw = meta.get('activeSolutionCode');
      const solutionExplanationRaw = meta.get('activeSolutionExplanation');
      const taskIdRaw = meta.get('activeTaskId');

      let tags = [];
      try {
        const parsed = typeof tagsJson === 'string' && tagsJson ? JSON.parse(tagsJson) : [];
        tags = Array.isArray(parsed) ? parsed.map((name) => ({ name })).filter((t) => typeof t?.name === 'string' && t.name.trim()) : [];
      } catch {
        tags = [];
      }

      let answer_spec = {};
      try {
        const parsedSpec = typeof answerSpecRaw === 'string' && answerSpecRaw ? JSON.parse(answerSpecRaw) : {};
        answer_spec = parsedSpec && typeof parsedSpec === 'object' && !Array.isArray(parsedSpec) ? parsedSpec : {};
      } catch {
        answer_spec = {};
      }

      const year = typeof yearRaw === 'number' ? yearRaw : (String(yearRaw || '').trim() ? Number(yearRaw) : null);
      const yearNorm = Number.isFinite(year) ? year : null;
      const taskId = typeof taskIdRaw === 'number' ? taskIdRaw : (String(taskIdRaw || '').trim() ? Number(taskIdRaw) : null);

      setActiveTaskTitle(typeof title === 'string' && title.trim() ? title : 'Текст задачи');
      setActiveTaskId(Number.isFinite(taskId) ? taskId : null);
      let attachments = [];
      try {
        const attJson = meta.get('activeTaskAttachmentsJson');
        const p = typeof attJson === 'string' && attJson ? JSON.parse(attJson) : [];
        attachments = Array.isArray(p) ? p : [];
      } catch {
        attachments = [];
      }
      setActiveTaskAttachments(attachments);
      setActiveTaskMeta({
        tags,
        source_name: typeof source_name === 'string' ? source_name : '',
        source_number: typeof source_number === 'string' ? source_number : '',
        source_author: typeof source_author === 'string' ? source_author : '',
        year: yearNorm,
        answer_type: typeof answerTypeRaw === 'string' ? answerTypeRaw : (answerTypeRaw != null ? String(answerTypeRaw) : ''),
        expected_answer: typeof expectedRaw === 'string' ? expectedRaw : (expectedRaw != null ? String(expectedRaw) : ''),
        answer_spec,
        solution_code: typeof solutionCodeRaw === 'string' ? solutionCodeRaw : (solutionCodeRaw != null ? String(solutionCodeRaw) : ''),
        solution_explanation: typeof solutionExplanationRaw === 'string'
          ? solutionExplanationRaw
          : (solutionExplanationRaw != null ? String(solutionExplanationRaw) : ''),
      });
    };
    apply();
    meta.observe(apply);
    return () => meta.unobserve(apply);
  }, [ydoc]);

  useEffect(() => {
    if (layoutStorageKey) setLayout(loadLayoutFromStorage(layoutStorageKey));
  }, [layoutStorageKey]);

  useEffect(() => {
    if (!inputStateKey) return;
    try {
      const s = localStorage.getItem(inputStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setInputCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setInputCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [inputStateKey]);

  useEffect(() => {
    if (!taskStatementStateKey) return;
    try {
      const s = localStorage.getItem(taskStatementStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setTaskStatementCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setTaskStatementCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [taskStatementStateKey]);

  useEffect(() => {
    if (!taskListStateKey) return;
    try {
      const s = localStorage.getItem(taskListStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setTaskListCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setTaskListCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [taskListStateKey]);

  useEffect(() => {
    if (!allTasksStateKey) return;
    try {
      const s = localStorage.getItem(allTasksStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setAllTasksCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setAllTasksCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [allTasksStateKey]);

  function saveInputState(collapsed, lastH) {
    try {
      localStorage.setItem(inputStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  function saveTaskStatementState(collapsed, lastH) {
    try {
      localStorage.setItem(taskStatementStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  function saveTaskListState(collapsed, lastH) {
    try {
      localStorage.setItem(taskListStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  function saveAllTasksState(collapsed, lastH) {
    try {
      localStorage.setItem(allTasksStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (sessionId) {
      setCurrentSessionId(sessionId);
      fetchSessionInfo(sessionId);
    }
  }, [sessionId]);

  // Глобальный хоткей Ctrl+Enter для запуска программы,
  // чтобы он стабильно работал даже если CodeMirror не перехватывает сочетание.
  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'Enter' || e.code === 'Enter' || e.keyCode === 13) && (e.ctrlKey || e.metaKey)) {
        const active = document.activeElement;
        // Не трогаем, если фокус в обычном input/textarea вне редактора
        if (active && active.closest && !active.closest('.collaborative-editor')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (runRequestRef.current && typeof runRequestRef.current.run === 'function') {
          runRequestRef.current.run();
        }
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []);

  async function fetchSessionInfo(id) {
    try {
      const res = await api.get(`/collaborative/sessions/${id}/`);
      setSessionInfo(res.data);
    } catch {
      setSessionInfo(null);
    }
  }

  async function createSession() {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/collaborative/sessions/', {
        title: 'Новая сессия',
      });
      const newId = res.data.session_id || res.data.id;
      navigate(`/collaborative/${newId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось создать сессию');
    } finally {
      setLoading(false);
    }
  }

  function handleJoin() {
    if (joinCode.trim()) {
      navigate(`/collaborative/${joinCode.trim()}`);
    }
  }

  function getDraftKey() {
    const uid = user?.id ?? user?.username ?? 'guest';
    const sid = currentSessionId || sessionId || '';
    return `draft:${uid}:collaborative:${sid}`;
  }

  function tryShowDraftPrompt() {
    if (draftCheckedRef.current) return;
    if (!user) return;
    const key = getDraftKey();
    if (!key || !currentSessionId) return;
    let saved = null;
    try {
      saved = localStorage.getItem(key);
    } catch {
      saved = null;
    }
    draftCheckedRef.current = true;
    if (typeof saved === 'string' && saved.length > 0 && saved !== code) {
      setDraftPrompt({ key, value: saved });
    }
  }

  useEffect(() => {
    tryShowDraftPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentSessionId]);

  useEffect(() => {
    if (!user) return;
    if (editorSettings?.autosaveDrafts === false) return;
    if (!currentSessionId) return;
    const key = getDraftKey();
    if (!key) return;
    if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    draftSaveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, code ?? '');
      } catch {
        // ignore
      }
    }, 800);
    return () => {
      if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, user?.id, currentSessionId, editorSettings?.autosaveDrafts]);

  function restoreDraft() {
    if (!draftPrompt?.value) return;
    const v = draftPrompt.value;
    if (ydoc) {
      const ytext = ydoc.getText('codemirror');
      ydoc.transact(() => {
        ytext.delete(0, ytext.length);
        ytext.insert(0, v);
      });
    } else {
      setCode(v);
    }
    setDraftPrompt(null);
  }

  function discardDraft() {
    const key = draftPrompt?.key;
    if (key) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
    setDraftPrompt(null);
  }

  async function handleToggleWrapping() {
    if (!user || wrapSaving) return;
    const previousSettings = editorSettings;
    const nextWrap = !taskTextWrapping;
    const nextSettings = {
      ...previousSettings,
      lineWrapping: nextWrap,
      taskTextWrapping: nextWrap,
    };
    setWrapSaveError('');
    setEditorSettings(nextSettings);
    setWrapSaving(true);
    try {
      await api.patch('/auth/profile/', { editor_settings: nextSettings });
    } catch {
      setEditorSettings(previousSettings);
      setWrapSaveError('Не удалось сохранить настройку переноса.');
    } finally {
      setWrapSaving(false);
    }
  }

  async function handleSetEditorTheme(themeId) {
    if (!user || editorThemeSaving || editorTheme === themeId) return;
    const previousSettings = editorSettings;
    const nextSettings = { ...previousSettings, themeId };
    setWrapSaveError('');
    setEditorSettings(nextSettings);
    setEditorThemeSaving(true);
    try {
      await api.patch('/auth/profile/', { editor_settings: nextSettings });
    } catch {
      setEditorSettings(previousSettings);
      setWrapSaveError('Не удалось сохранить тему редактора.');
    } finally {
      setEditorThemeSaving(false);
    }
  }

  const inviteUrl = currentSessionId
    ? `${window.location.origin}/collaborative/${currentSessionId}`
    : '';

  // Hooks must run before any early return (React #310 when joining a session).
  useEffect(() => {
    setSolutionCopyHint('');
    if (solutionCopyHintTimerRef.current) {
      clearTimeout(solutionCopyHintTimerRef.current);
      solutionCopyHintTimerRef.current = null;
    }
  }, [activeTaskId, activeTaskMeta.solution_code, activeTaskMeta.solution_explanation]);

  useEffect(() => () => {
    if (solutionCopyHintTimerRef.current) {
      clearTimeout(solutionCopyHintTimerRef.current);
    }
  }, []);

  if (!currentSessionId) {
    return (
      <div className="collaborative-page">
        <div className="page-header">
          <h1>Совместное кодирование</h1>
          <p className="page-subtitle">Работайте над кодом вместе с другими участниками</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="collab-actions">
          <div className="collab-action-card">
            <h3>Создать сессию</h3>
            <p>Создайте новую сессию совместного кодирования</p>
            <button onClick={createSession} className="btn btn-primary" disabled={loading}>
              {loading ? 'Создание...' : '➕ Создать сессию'}
            </button>
          </div>

          <div className="collab-action-card">
            <h3>Присоединиться</h3>
            <p>Введите код сессии для подключения</p>
            <div className="join-form">
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Код сессии"
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              />
              <button onClick={handleJoin} className="btn btn-secondary" disabled={!joinCode.trim()}>
                Войти
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const header = sessionInfo?.title ? (
    <div className="session-info">
      <span className="session-title">{sessionInfo.title}</span>
    </div>
  ) : null;

  const taskStatementBlock = (
    <div className="work-block-statement-content">
      <CollaborativeTaskPanel ydoc={ydoc} editMode={taskTextEditMode} wrapEnabled={taskTextWrapping} />
      {isChoiceAnswerType(activeTaskMeta.answer_type) ? (
        <ChoiceAnswerPanel
          answerType={activeTaskMeta.answer_type}
          answerSpec={activeTaskMeta.answer_spec}
          expectedAnswer={activeTaskMeta.expected_answer}
          taskKey={activeTaskId != null ? String(activeTaskId) : activeTaskTitle}
        />
      ) : null}
    </div>
  );

  const codeEditorBlock = (
    <div className="task-work-editor collaborative">
      <UnifiedEditor
        ref={editorRef}
        mode="collaborative"
        sessionId={currentSessionId}
        onContentChange={setCode}
        theme={editorTheme}
        editorSettings={editorSettings}
        onYdocReady={setYdoc}
        onRunRequest={() => runRequestRef.current?.run?.()}
        onStatusChange={({ connected, userCount }) => {
          setCollabConnected(connected);
          setCollabUserCount(userCount);
        }}
        styleLintEnabled={styleLintEnabled}
        onStyleLintChange={setStyleLintEnabled}
        onFormattingChange={setFormatting}
      />
    </div>
  );

  const taskListBlock = (
    <CollaborativeSessionTaskList
      sessionId={currentSessionId}
      isTeacher={canManageCollabTasks}
      ydoc={ydoc}
      refreshNonce={taskListRefreshNonce}
      onActiveTaskChange={({
        title,
        taskId,
        task_tags,
        source_name,
        source_number,
        source_author,
        year,
        answer_type,
        expected_answer,
        answer_spec,
        solution_code,
        solution_explanation,
      }) => {
        setActiveTaskTitle(title || 'Текст задачи');
        setActiveTaskId(typeof taskId === 'number' ? taskId : null);
        setActiveTaskMeta({
          tags: Array.isArray(task_tags) ? task_tags.map((name) => ({ name })) : [],
          source_name: source_name || '',
          source_number: source_number || '',
          source_author: source_author || '',
          year: year ?? null,
          answer_type: answer_type || '',
          expected_answer: expected_answer || '',
          answer_spec: answer_spec && typeof answer_spec === 'object' ? answer_spec : {},
          solution_code: solution_code || '',
          solution_explanation: solution_explanation || '',
        });
      }}
    />
  );

  const allTasksBlock = canManageCollabTasks ? (
    <CollaborativeAllTasksPanel
      ref={allTasksPanelRef}
      sessionId={currentSessionId}
      refreshNonce={allTasksRefreshNonce}
      onAdded={() => setTaskListRefreshNonce((n) => n + 1)}
    />
  ) : null;

  const inputBlockContent = <CollaborativeRunnerInputSection />;

  function applyPresetA() {
    const next = LAYOUT_PRESET_A.map((item) => ({ ...item }));
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'A');
  }

  function applyPresetB() {
    const next = LAYOUT_PRESET_B.map((item) => ({ ...item }));
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'B');
  }

  function handleLayoutChange(next) {
    if (layoutsEqual(next, layout)) return;
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'custom');
    const inputItem = next.find((i) => i.i === 'inputBlock');
    if (inputItem && !inputCollapsed && typeof inputItem.h === 'number') {
      setInputCollapsedHeight(inputItem.h);
      saveInputState(false, inputItem.h);
    }
    const taskStatementItem = next.find((i) => i.i === 'taskStatement');
    if (taskStatementItem && !taskStatementCollapsed && typeof taskStatementItem.h === 'number') {
      setTaskStatementCollapsedHeight(taskStatementItem.h);
      saveTaskStatementState(false, taskStatementItem.h);
    }
    const taskListItem = next.find((i) => i.i === 'taskList');
    if (taskListItem && !taskListCollapsed && typeof taskListItem.h === 'number') {
      setTaskListCollapsedHeight(taskListItem.h);
      saveTaskListState(false, taskListItem.h);
    }
  }

  const solutionForClipboard = solutionClipboardText(activeTaskMeta);

  async function handleCopySolution() {
    const text = solutionClipboardText(activeTaskMeta);
    if (!text) {
      setSolutionCopyHint('Нет решения');
      if (solutionCopyHintTimerRef.current) clearTimeout(solutionCopyHintTimerRef.current);
      solutionCopyHintTimerRef.current = setTimeout(() => setSolutionCopyHint(''), 2000);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setSolutionCopyHint('Скопировано');
    } catch {
      setSolutionCopyHint('Не удалось скопировать');
    }
    if (solutionCopyHintTimerRef.current) clearTimeout(solutionCopyHintTimerRef.current);
    solutionCopyHintTimerRef.current = setTimeout(() => setSolutionCopyHint(''), 2000);
  }

  const effectiveLayout = layout.map((item) => {
    if (item.i === 'inputBlock') return { ...item, h: inputCollapsed ? 1 : (inputCollapsedHeight ?? 6) };
    if (item.i === 'taskStatement') return { ...item, h: taskStatementCollapsed ? 1 : (taskStatementCollapsedHeight ?? 6) };
    if (item.i === 'taskList') return { ...item, h: taskListCollapsed ? 1 : (taskListCollapsedHeight ?? 9) };
    if (item.i === 'allTasks') return { ...item, h: allTasksCollapsed ? 1 : (allTasksCollapsedHeight ?? 9) };
    return item;
  });

  function onInputCollapseToggle() {
    const inputItem = effectiveLayout.find((i) => i.i === 'inputBlock');
    const prevH = inputItem?.h ?? 6;
    setInputCollapsedHeight(prevH);
    setInputCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'inputBlock' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveInputState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onInputExpand() {
    setInputCollapsed(false);
    const h = inputCollapsedHeight ?? 6;
    const nextLayout = layout.map((i) => (i.i === 'inputBlock' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveInputState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskStatementCollapseToggle() {
    const item = effectiveLayout.find((i) => i.i === 'taskStatement');
    const prevH = item?.h ?? 6;
    setTaskStatementCollapsedHeight(prevH);
    setTaskStatementCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'taskStatement' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveTaskStatementState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskStatementExpand() {
    setTaskStatementCollapsed(false);
    const h = taskStatementCollapsedHeight ?? 6;
    const nextLayout = layout.map((i) => (i.i === 'taskStatement' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveTaskStatementState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskListCollapseToggle() {
    const item = effectiveLayout.find((i) => i.i === 'taskList');
    const prevH = item?.h ?? 9;
    setTaskListCollapsedHeight(prevH);
    setTaskListCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'taskList' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveTaskListState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskListExpand() {
    setTaskListCollapsed(false);
    const h = taskListCollapsedHeight ?? 9;
    const nextLayout = layout.map((i) => (i.i === 'taskList' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveTaskListState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onAllTasksCollapseToggle() {
    const item = effectiveLayout.find((i) => i.i === 'allTasks');
    const prevH = item?.h ?? 9;
    setAllTasksCollapsedHeight(prevH);
    setAllTasksCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'allTasks' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveAllTasksState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onAllTasksExpand() {
    setAllTasksCollapsed(false);
    const h = allTasksCollapsedHeight ?? 9;
    const nextLayout = layout.map((i) => (i.i === 'allTasks' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveAllTasksState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  return (
    <div className={`collaborative-page ${layoutEditMode ? 'layout-edit-mode' : ''}`}>
      {wrapSaveError && <div className="alert alert-warning">{wrapSaveError}</div>}
      {draftPrompt && (
        <div className="alert alert-warning" style={{ marginBottom: '0.75rem' }}>
          Найден несохранённый черновик для этой сессии.
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-sm btn-primary" onClick={restoreDraft}>Восстановить</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={discardDraft}>Удалить</button>
          </div>
        </div>
      )}
      <CollaborativeRunnerProvider code={code} ydoc={ydoc} runRef={runRequestRef}>
        <RunnerSessionTaskAttachmentsSync ydoc={ydoc} />
        {header}
        <Modal open={inputModalOpen} title="Input" onClose={() => setInputModalOpen(false)}>
          {inputBlockContent}
        </Modal>
        <CollabPageWithToolbar
          collabConnected={collabConnected}
          collabUserCount={collabUserCount}
          styleLintEnabled={styleLintEnabled}
          onStyleLintChange={setStyleLintEnabled}
          onFormatClick={() => editorRef.current?.format?.()}
          formatting={formatting}
          inviteUrl={inviteUrl}
          onCopyInvite={() => inviteUrl && navigator.clipboard.writeText(inviteUrl)}
          layoutEditMode={layoutEditMode}
          onLayoutEditModeChange={setLayoutEditMode}
          onLayoutPresetA={applyPresetA}
          onLayoutPresetB={applyPresetB}
          onRunClick={() => runRequestRef.current?.run?.()}
          layoutStorageKey={layoutStorageKey}
          layout={effectiveLayout}
          onLayoutChange={handleLayoutChange}
          inputCollapsed={inputCollapsed}
          onInputCollapseToggle={onInputCollapseToggle}
          onInputExpand={onInputExpand}
          taskStatementCollapsed={taskStatementCollapsed}
          onTaskStatementCollapseToggle={onTaskStatementCollapseToggle}
          onTaskStatementExpand={onTaskStatementExpand}
          taskListCollapsed={taskListCollapsed}
          onTaskListCollapseToggle={onTaskListCollapseToggle}
          onTaskListExpand={onTaskListExpand}
          allTasksCollapsed={allTasksCollapsed}
          onAllTasksCollapseToggle={onAllTasksCollapseToggle}
          onAllTasksExpand={onAllTasksExpand}
          showAllTasksBlock={canManageCollabTasks}
          taskListHeaderActions={
            <button
              type="button"
              className="btn btn-outline btn-xs btn-icon-only"
              title="Обновить"
              aria-label="Обновить"
              onClick={() => setTaskListRefreshNonce((n) => n + 1)}
            >
              <i className="fa-solid fa-rotate-right ui-icon" aria-hidden />
            </button>
          }
          allTasksTitle="Каталог задач"
          allTasksHeaderActions={
            <>
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Поиск задач"
                aria-label="Поиск задач"
                onClick={() => allTasksPanelRef.current?.openSearchModal?.()}
              >
                <i className="fa-solid fa-magnifying-glass ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Развернуть текст всех задач"
                aria-label="Развернуть текст всех задач"
                onClick={() => allTasksPanelRef.current?.expandAllStatements?.()}
              >
                <i className="fa-solid fa-angles-down ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Свернуть текст всех задач"
                aria-label="Свернуть текст всех задач"
                onClick={() => allTasksPanelRef.current?.collapseAllStatements?.()}
              >
                <i className="fa-solid fa-angles-up ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Обновить"
                aria-label="Обновить"
                onClick={() => setAllTasksRefreshNonce((n) => n + 1)}
              >
                <i className="fa-solid fa-rotate-right ui-icon" aria-hidden />
              </button>
            </>
          }
          taskStatement={taskStatementBlock}
          taskStatementTitle={
            <span className="work-block-title-with-meta">
              <span className="work-block-title-text">{activeTaskTitle || 'Текст задачи'}</span>
              <TaskStatementMeta
                variant="header"
                tags={activeTaskMeta.tags}
                source_name={activeTaskMeta.source_name}
                source_number={activeTaskMeta.source_number}
                source_author={activeTaskMeta.source_author}
                year={activeTaskMeta.year}
              />
            </span>
          }
          taskStatementSupplement={
            <StatementAttachmentLinks attachments={activeTaskAttachments} variant="work" />
          }
          taskStatementHeaderActions={
            <>
              {!isChoiceAnswerType(activeTaskMeta.answer_type) ? (
                <TaskAnswerCheckPanel
                  variant="header"
                  expectedAnswer={activeTaskMeta.expected_answer}
                  taskKey={activeTaskId != null ? String(activeTaskId) : activeTaskTitle}
                />
              ) : null}
              {solutionForClipboard ? (
                <button
                  type="button"
                  className="btn btn-outline btn-xs work-block-title-action-wrap"
                  onClick={() => void handleCopySolution()}
                  title="Скопировать эталонное решение в буфер обмена"
                  aria-label="Скопировать эталонное решение в буфер обмена"
                >
                  {solutionCopyHint || 'Решение'}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-outline btn-xs work-block-title-action-wrap"
                onClick={handleToggleWrapping}
                disabled={wrapSaving || !user}
                title={taskTextWrapping ? 'Выключить перенос строк' : 'Включить перенос строк'}
                aria-label={taskTextWrapping ? 'Перенос включен' : 'Перенос выключен'}
              >
                {taskTextWrapping ? 'Перенос: вкл' : 'Перенос: выкл'}
              </button>
              {!taskTextEditMode ? (
                <button
                  type="button"
                  className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                  title="Редактировать"
                  aria-label="Редактировать"
                  onClick={() => setTaskTextEditMode(true)}
                >
                  <i className="fa-solid fa-pen ui-icon" aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-xs btn-icon-only work-block-title-action"
                  title="Сохранить"
                  aria-label="Сохранить"
                  onClick={() => setTaskTextEditMode(false)}
                >
                  <i className="fa-solid fa-floppy-disk ui-icon" aria-hidden />
                </button>
              )}
            </>
          }
          codeEditor={codeEditorBlock}
          taskList={taskListBlock}
          allTasks={allTasksBlock}
          onOpenInput={() => setInputModalOpen(true)}
          outputBlock={<CollaborativeRunnerOutputSection />}
          outputBlockRunInfo={<OutputBlockRunInfo />}
        />
      </CollaborativeRunnerProvider>
    </div>
  );
}

function CollabPageWithToolbar({
  collabConnected,
  collabUserCount,
  styleLintEnabled,
  onStyleLintChange,
  onFormatClick,
  formatting,
  inviteUrl,
  onCopyInvite,
  layoutEditMode,
  onLayoutEditModeChange,
  onLayoutPresetA,
  onLayoutPresetB,
  onRunClick,
  layoutStorageKey,
  layout,
  onLayoutChange,
  inputCollapsed,
  onInputCollapseToggle,
  onInputExpand,
  taskStatementCollapsed,
  onTaskStatementCollapseToggle,
  onTaskStatementExpand,
  taskListCollapsed,
  onTaskListCollapseToggle,
  onTaskListExpand,
  allTasksCollapsed,
  onAllTasksCollapseToggle,
  onAllTasksExpand,
  showAllTasksBlock = true,
  taskListHeaderActions,
  allTasksTitle,
  allTasksHeaderActions,
  taskStatementTitle,
  taskStatementSupplement,
  taskStatementHeaderActions,
  taskStatement,
  codeEditor,
  taskList,
  allTasks,
  onOpenInput,
  outputBlock,
}) {
  const { loading, autoClearOutput, setAutoClearOutput, clearOutput } = useRunnerContext();
  const { setToolbarProps, clearToolbarProps } = useWorkChrome();
  const toolbarHandlersRef = useRef({});
  toolbarHandlersRef.current = {
    onStyleLintChange,
    onFormatClick,
    onCopyInvite,
    onLayoutEditModeChange,
    onLayoutPresetA,
    onLayoutPresetB,
    setAutoClearOutput,
    clearOutput,
  };

  useEffect(() => {
    setToolbarProps({
      connected: collabConnected,
      userCount: collabUserCount,
      styleLintEnabled,
      formatting,
      inviteUrl,
      layoutEditMode,
      autoClearOutput,
      onStyleLintChange: (v) => toolbarHandlersRef.current.onStyleLintChange?.(v),
      onFormatClick: () => toolbarHandlersRef.current.onFormatClick?.(),
      onCopyInvite: () => toolbarHandlersRef.current.onCopyInvite?.(),
      onLayoutEditModeChange: (v) => toolbarHandlersRef.current.onLayoutEditModeChange?.(v),
      onLayoutPresetA: () => toolbarHandlersRef.current.onLayoutPresetA?.(),
      onLayoutPresetB: () => toolbarHandlersRef.current.onLayoutPresetB?.(),
      onAutoClearOutputChange: (v) => toolbarHandlersRef.current.setAutoClearOutput?.(v),
      onClearOutput: () => toolbarHandlersRef.current.clearOutput?.(),
    });
  }, [
    setToolbarProps,
    collabConnected,
    collabUserCount,
    styleLintEnabled,
    formatting,
    inviteUrl,
    layoutEditMode,
    autoClearOutput,
  ]);

  useEffect(() => () => clearToolbarProps(), [clearToolbarProps]);

  return (
    <>
      <WorkPageBlocksLayout
        taskStatementTitle={taskStatementTitle}
        taskStatementSupplement={taskStatementSupplement}
        taskStatementHeaderActions={taskStatementHeaderActions}
        taskStatement={taskStatement}
        codeEditor={codeEditor}
        codeEditorHeaderActions={
          <>
            <button
              type="button"
              className={`btn btn-small work-toolbar-btn work-toolbar-run work-block-title-action ${loading ? 'running' : ''}`}
              onClick={onRunClick}
              disabled={loading}
              title={loading ? 'Остановить программу (пока не реализовано)' : 'Запустить программу (Ctrl+Enter)'}
              aria-label={loading ? 'Выполняется' : 'Запустить'}
            >
              <i className={`fa-solid ${loading ? 'fa-stop' : 'fa-play'} ui-icon`} aria-hidden />
            </button>
            <span className="work-block-title-input-files" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
              <EditorInputFilesBadge />
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Ввод и файлы"
                aria-label="Ввод и файлы"
                onClick={onOpenInput}
              >
                <i className="fa-solid fa-keyboard ui-icon" aria-hidden />
              </button>
            </span>
          </>
        }
        taskList={taskList}
        allTasks={allTasks}
        allTasksTitle={allTasksTitle}
        allTasksHeaderActions={allTasksHeaderActions}
        showAllTasksBlock={showAllTasksBlock}
        outputBlock={outputBlock}
        outputBlockRunInfo={<OutputBlockRunInfo />}
        editMode={layoutEditMode}
        layoutStorageKey={layoutStorageKey}
        layout={layout}
        onLayoutChange={onLayoutChange}
        inputCollapsed={inputCollapsed}
        onInputCollapseToggle={onInputCollapseToggle}
        onInputExpand={onInputExpand}
        taskStatementCollapsed={taskStatementCollapsed}
        onTaskStatementCollapseToggle={onTaskStatementCollapseToggle}
        onTaskStatementExpand={onTaskStatementExpand}
        taskListCollapsed={taskListCollapsed}
        onTaskListCollapseToggle={onTaskListCollapseToggle}
        onTaskListExpand={onTaskListExpand}
        allTasksCollapsed={allTasksCollapsed}
        onAllTasksCollapseToggle={onAllTasksCollapseToggle}
        onAllTasksExpand={onAllTasksExpand}
        taskListHeaderActions={taskListHeaderActions}
      />
    </>
  );
}
